import { Search, X } from 'lucide-react';
import type { MenuCategoryItem } from '@/data';
import { useLanguage } from '@/contexts/AppContext';

interface MenuCategoryTabsProps {
  categories: MenuCategoryItem[];
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
  isSearchMode: boolean;
  onSearchModeToggle: (isSearch: boolean) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  translations: {
    categories: Record<string, string>;
  };
}

export function MenuCategoryTabs({
  categories,
  selectedCategory,
  onCategorySelect,
  isSearchMode,
  onSearchModeToggle,
  searchQuery,
  onSearchChange,
  translations,
}: MenuCategoryTabsProps) {
  const { language } = useLanguage();
  const handleCategoryClick = (category: string) => {
    onCategorySelect(category);
    // 카테고리 섹션으로 스크롤 (sticky 헤더 오프셋 적용)
    const categoryElement = document.getElementById(`category-${category}`);
    if (categoryElement) {
      const yOffset = -120;
      const y = categoryElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const getSearchPlaceholder = () => {
    switch (language) {
      case 'ko':
        return '메뉴 검색...';
      case 'es':
        return 'Buscar menú...';
      default:
        return 'Search menu...';
    }
  };

  return (
    <div className="sticky top-[50px] z-30 bg-background border-b border-border shadow-sm">
      <div className="relative">
        {isSearchMode ? (
          /* 검색 모드 */
          <div className="flex items-center gap-[clamp(8px,2vw,12px)] p-[clamp(8px,2vw,16px)]">
            <button
              onClick={() => {
                onSearchModeToggle(false);
                onSearchChange('');
              }}
              className="shrink-0 p-[clamp(6px,1.5vw,10px)] rounded-full hover:bg-muted transition-colors duration-200 active:scale-95"
            >
              <X className="w-[clamp(16px,4vw,20px)] h-[clamp(16px,4vw,20px)] text-muted-foreground" />
            </button>
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={getSearchPlaceholder()}
                className="w-full px-[clamp(12px,3vw,16px)] py-[clamp(8px,2vw,12px)] rounded-full bg-muted text-foreground text-[clamp(11px,2.5vw,14px)] outline-none focus:ring-2 focus:ring-border transition-all duration-200"
                autoFocus
              />
            </div>
          </div>
        ) : (
          /* 카테고리 모드 */
          <div className="flex overflow-x-auto scrollbar-hide p-[clamp(8px,2vw,16px)] gap-[clamp(6px,1.5vw,12px)] scroll-smooth">
            {/* 검색 아이콘 버튼 */}
            <button
              onClick={() => onSearchModeToggle(true)}
              className="shrink-0 w-[clamp(32px,8vw,40px)] h-[clamp(32px,8vw,40px)] rounded-full bg-background flex items-center justify-center shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200 border border-border"
            >
              <Search className="w-[clamp(14px,3.5vw,18px)] h-[clamp(14px,3.5vw,18px)] text-muted-foreground" />
            </button>

            {/* 카테고리 버튼들 */}
            {categories.map(category => {
              // API 카테고리는 _name 사용, 정적 카테고리는 번역 시스템 사용
              const categoryName = category._name || translations.categories[category.nameKey] || category.nameKey;
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`px-[clamp(10px,2.4vw,16px)] py-[clamp(4px,1vw,8px)] rounded-[5px] text-[clamp(9px,2vw,11px)] font-medium transition-all duration-200 font-['Noto_Sans',sans-serif] hover:scale-105 transform shrink-0 whitespace-nowrap shadow-sm active:scale-95 ${
                    selectedCategory === category.id
                      ? 'bg-primary text-white shadow-md border-2 border-primary'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:shadow-md border-2 border-transparent'
                  }`}
                >
                  {categoryName}
                </button>
              );
            })}
          </div>
        )}

        {/* 그라데이션 페이드 인디케이터 - 카테고리 모드에서만 표시 */}
        {!isSearchMode && (
          <>
            <div className="absolute left-0 top-0 bottom-0 w-[clamp(16px,4vw,24px)] bg-gradient-to-r from-background to-transparent pointer-events-none opacity-50"></div>
            <div className="absolute right-0 top-0 bottom-0 w-[clamp(16px,4vw,24px)] bg-gradient-to-l from-background to-transparent pointer-events-none opacity-50"></div>
          </>
        )}
      </div>
    </div>
  );
}
